import { Box, Drawer, Tab, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import DiamondIcon from '@mui/icons-material/Diamond';
import { ITeamUser } from '@common/types';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { ManageTeam } from './components/ManageTeam';
import { SidebarMember } from './components/SidebarMember';
import { AddMembers } from './AddMembers';
import { useTeamDrawer } from './hooks/useTeamDrawer';
import { t } from 'i18next';
import { TransferMembers } from './TransferMembers';
import { History } from 'components/History';
import { TruncatedTextWithConditionalTooltip } from 'components';

interface ITeamTableProps {
  teamId: string;
  isOpen: boolean;
  handleClose: () => void;
}

export const TeamDrawer = ({
  teamId,
  isOpen,
  handleClose,
}: ITeamTableProps) => {
  const {
    teamData,
    teamHistory,
    historyPage,
    setHistoryPage,
    handleCloseTrigger,
    tabIndex,
    handleTriggerChangeTab,
    historyTabIndex,
  } = useTeamDrawer({
    id: teamId,
    handleClose,
  });

  return (
    <Drawer anchor={'right'} open={isOpen} onClose={handleCloseTrigger}>
      <Box
        sx={{ minWidth: '600px', maxWidth: '600px', paddingY: 5, paddingX: 3 }}
      >
        <Box sx={{ display: 'flex', paddingBottom: 5 }}>
          <DiamondIcon
            sx={{
              width: 56,
              height: 56,
              marginRight: 2,
              color: teamData?.color,
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: 'text.primary',
                  fontWeight: 'fontWeightMedium',
                  fontSize: '1.25rem',
                  lineHeight: '32px',
                }}
              >
                {teamData?.name}
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontWeight: 'fontWeightRegular',
                  fontSize: '0.87rem',
                  lineHeight: '20px',
                }}
              >
                {teamData?.description ? (
                  <TruncatedTextWithConditionalTooltip
                    variant='body2'
                    text={teamData?.description}
                    width='350px'
                  />
                ) : (
                  <>{t('adminPanel.teams.drawer.title')}</>
                )}
              </Typography>
            </Box>
            <Box
              onClick={handleCloseTrigger}
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                ':hover': {
                  cursor: 'pointer',
                  bgcolor: theme => theme.palette.grey[50],
                  borderRadius: '6px',
                },
                height: '24px',
                width: '24px',
              }}
            >
              <ClearIcon />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingBottom: '24px',
            gap: 1,
          }}
        >
          <AddMembers teamId={teamId} />
          <TransferMembers
            teamId={teamId}
            teamColor={teamData?.color}
            teamName={teamData?.name}
          />
        </Box>
        <Box sx={{ width: '100%', typography: 'body1' }}>
          <TabContext value={tabIndex}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList onChange={handleTriggerChangeTab}>
                <Tab
                  label={t('adminPanel.teams.drawer.generalTab.tabTitle')}
                  value='1'
                />
                <Tab
                  label={`${t('adminPanel.teams.drawer.membersTab.tabTitle')} (${teamData?.users.length})`}
                  value='2'
                />
                <Tab
                  label={t('adminPanel.teams.drawer.historyTab.tabTitle')}
                  value={historyTabIndex}
                />
              </TabList>
            </Box>
            <TabPanel value='1'>
              <Box
                sx={{
                  paddingTop: 1,
                  maxHeight: 'calc(100vh - 300px)',
                  overflowY: 'auto',
                }}
              >
                <ManageTeam teamId={teamId} />
              </Box>
            </TabPanel>
            <TabPanel value='2'>
              <Box
                sx={{
                  paddingTop: 1,
                  maxHeight: 'calc(100vh - 300px)',
                  overflowY: 'auto',
                }}
              >
                {teamData?.users.map((item: ITeamUser) => (
                  <SidebarMember key={item.id} user={item} teamId={teamId} />
                ))}
              </Box>
            </TabPanel>
            <TabPanel value={historyTabIndex}>
              <Box
                sx={{
                  paddingTop: 1,
                  maxHeight: 'calc(100vh - 300px)',
                  overflowY: 'auto',
                }}
              >
                {teamHistory && (
                  <History
                    page={historyPage}
                    changePage={setHistoryPage}
                    history={teamHistory.history}
                    count={teamHistory.count}
                  />
                )}
              </Box>
            </TabPanel>
          </TabContext>
        </Box>
      </Box>
    </Drawer>
  );
};
